// src/services/api/client.ts
import { refreshAuthToken } from '../firebase';
import { config } from '../config';

// Use environment variable for API base URL
export const API_BASE_URL = config.apiUrl;

/**
 * Checks if a response indicates an expired token (401 Unauthorized)
 * @param response The fetch response object
 * @returns True if the token is expired, false otherwise
 */
export const isTokenExpired = (response: Response): boolean => {
  return response.status === 401;
};

/**
 * An Error carrying the HTTP status of the response that produced it.
 *
 * Callers that need to branch on the status — e.g. "404 means there is no
 * pending edit, which is a normal outcome, not a failure" — read `status`
 * instead of pattern-matching the message. Plain `Error` shape is preserved
 * (`instanceof Error`, `.message`) so existing catch blocks are unaffected.
 *
 * Deliberately not a `class ... extends Error`: the app compiles to ES5, where
 * subclassing a native breaks the prototype chain and `instanceof` with it.
 */
export interface ApiError extends Error {
  status?: number;
}

export const createApiError = (message: string, status: number): ApiError => {
  const error: ApiError = new Error(message);
  error.status = status;
  return error;
};

/**
 * The HTTP status attached to an error by `createApiError`, or undefined for
 * network errors and anything not thrown by the API layer.
 */
export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const status = (error as ApiError).status;
  return typeof status === 'number' ? status : undefined;
};

// Simple token refresh lock to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 10000; // 10 seconds between refresh attempts

/**
 * Utility function to fetch data with automatic token refresh if needed
 * @param url The URL to fetch
 * @param token The authentication token
 * @param options Fetch options
 * @param retryCount Number of retry attempts (default: 3)
 * @param retryDelay Delay between retries in ms (default: 1000)
 * @returns Promise resolving to the JSON response
 */
export const fetchWithTokenRefresh = async (
  url: string,
  token: string,
  options: RequestInit = {},
  retryCount: number = 3,
  retryDelay: number = 1000
): Promise<any> => {
  
  // Create request options with the token
  const requestOptions: RequestInit = {
    ...options,
    headers: {
      ...(options.headers as Record<string, string>),
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
  
  // Try the request with retries
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= retryCount; attempt++) {
    try {
      // If this is a retry attempt, wait before trying again
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
      
      // Make the request
      let response = await fetch(url, requestOptions);
      
      // If the response is successful, return the parsed JSON
      if (response.ok) {
        // Handle 204 No Content - no response body to parse
        if (response.status === 204) {
          return undefined;
        }
        return response.json();
      }
      
      // Try to get error message from response
      let errorMessage = `API error: ${response.status}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorJson = JSON.parse(errorText);
            if (errorJson.detail) {
              errorMessage = errorJson.detail;
            }
          } catch {
            // Not JSON, use text as-is if it's short enough
            if (errorText.length < 200) {
              errorMessage = errorText;
            }
          }
        }
      } catch (e) {
        // Ignore errors when trying to read error text
      }
      
      // If the token is expired, try to refresh it
      if (isTokenExpired(response)) {
        try {
          // Check if we're already refreshing or if we've refreshed recently
          const now = Date.now();
          if (!isRefreshing && (now - lastRefreshTime) > REFRESH_COOLDOWN) {
            // Set the lock to prevent other requests from refreshing
            isRefreshing = true;
            lastRefreshTime = now;
            
            try {
              // Try to refresh the token
              const newToken = await refreshAuthToken();
              
              if (newToken) {
                // Update the authorization header with the new token
                const newRequestOptions: RequestInit = {
                  ...requestOptions,
                  headers: {
                    ...(requestOptions.headers as Record<string, string>),
                    'Authorization': `Bearer ${newToken}`
                  }
                };
                
                // Try the request again with the new token
                const retryResponse = await fetch(url, newRequestOptions);
                
                if (retryResponse.ok) {
                  // Handle 204 No Content - no response body to parse
                  if (retryResponse.status === 204) {
                    return undefined;
                  }
                  return retryResponse.json();
                }
                
                // If still not successful, throw an error
                throw new Error(`API error after token refresh: ${retryResponse.status}`);
              }
            } finally {
              // Release the lock
              isRefreshing = false;
            }
          } else {
            // Another request is already refreshing or we've refreshed recently
            // Wait a bit and then continue with the retry loop
            await new Promise(resolve => setTimeout(resolve, 1000));
            continue;
          }
        } catch (refreshError) {
          console.error('Error refreshing token:', refreshError);
          // Continue with retry loop
        }
      }
      
      // If we get here, the request failed for a reason other than an expired token
      // or token refresh failed
      lastError = createApiError(errorMessage, response.status);
      
      // Continue with retry loop
    } catch (error: any) {
      // Network error or other exception
      lastError = error;
      
      // Continue with retry loop
    }
  }
  
  // If we've exhausted all retries, throw the last error
  if (lastError) {
    throw lastError;
  }
  
  // This should never happen, but TypeScript requires a return value
  throw new Error('Unexpected error in fetchWithTokenRefresh');
};
